import { Component, OnInit } from '@angular/core';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { Image } from '@ks89/angular-modal-gallery';
import { NgbRatingConfig } from '@ng-bootstrap/ng-bootstrap';
import { ProductsService } from 'src/app/shared/service/product.service';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import * as Notiflix from 'notiflix';
import { HttpEventType, HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-ebook-detail',
  templateUrl: './ebook-detail.component.html',
  styleUrl: './ebook-detail.component.scss',
  providers: [NgbRatingConfig]
})
export class EbookDetailComponent {
  getProgressBarType(progress: number): string {
    if (progress < 50) {
      return 'danger'; // Rojo si es menor de 50%
    } else if (progress >= 50 && progress < 80) {
      return 'warning'; // Amarillo entre 50% y 80%
    } else {
      return 'success'; // Verde si es mayor de 80%
    }
  }
  
  handleImageError(event: any) {
    console.warn('❌ Error al cargar imagen del ebook, usando placeholder');
    event.target.src = 'assets/images/placeholder.png';
  }
  
  // Método para obtener la imagen del ebook (frontImage.base64 o ImgFileUrl)
  getEbookImage(): string {
    if (this.productInfo?.frontImage?.base64) {
      const mimetype = this.productInfo.frontImage.mimetype || 'image/jpeg';
      const base64Image = `data:${mimetype};base64,${this.productInfo.frontImage.base64}`;
      console.log('🖼️ Usando frontImage.base64 del ebook ID:', this.productInfo.id);
      return base64Image;
    }
    
    if (this.productInfo?.ImgFileUrl) {
      console.log('🖼️ Usando ImgFileUrl del ebook');
      return this.productInfo.ImgFileUrl;
    }
    
    console.warn('⚠️ No hay imagen disponible para el ebook ID:', this.productInfo?.id);
    return ''; // Retornar string vacío en lugar de placeholder
  }
  
  // Método para obtener la URL del PDF para el visor
  getPdfUrl(): string | null {
    // Usar file.base64 para el PDF
    if (this.productInfo?.file?.base64) {
      const mimetype = this.productInfo.file.mimetype || 'application/pdf';
      const base64Pdf = `data:${mimetype};base64,${this.productInfo.file.base64}`;
      console.log('📄 PDF encontrado en file.base64 del ebook ID:', this.productInfo.id);
      console.log('📄 Nombre del archivo:', this.productInfo.file.filename);
      return base64Pdf;
    }
    
    console.warn('⚠️ No se encontró archivo PDF en file.base64 para el ebook ID:', this.productInfo?.id);
    return null;
  }
  
  productInfo: any;
  archivoUrl: SafeResourceUrl | null = null;
  isAdmin: boolean = false;

  constructor(
    private route: Router,
    private productService: ProductsService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) { }

  verArchivoMicrosoft(url: string): void {
    const fullUrl = `http://docs.google.com/gview?url=${url}&embedded=true`;
    console.log("Mostrando archivo en:", fullUrl);
    // Sanitiza la URL antes de asignarla al iframe
    window.open(fullUrl, '_blank');
  }     

  ngOnInit(): void {
    // Verificar si el usuario es admin
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    this.isAdmin = user.roles === 'admin';
    
    this.loadData();
  }
  
  async loadData() {
    try {
      const id = this.activatedRoute.snapshot.paramMap.get('id');
      
      if (!id) {
        console.error('❌ No se encontró el ID del ebook');
        Notiflix.Notify.failure('No se pudo cargar el ebook');
        this.route.navigateByUrl('/products/ebooks');
        return;
      }

      console.log('📥 Cargando ebook con ID:', id);
      const query = this.productService.getEbookById(id);
      const response = await lastValueFrom(query);
      
      console.log('✅ Respuesta del servidor:', response);
      
      // Validar que la respuesta tenga la estructura esperada
      if (!response) {
        console.error('❌ Respuesta vacía del servidor');
        Notiflix.Notify.failure('No se pudo cargar el ebook');
        this.route.navigateByUrl('/products/ebooks');
        return;
      }

      this.productInfo = response;

      // Log de la estructura recibida
      console.log('📦 Estructura del ebook:', {
        id: this.productInfo.id,
        title: this.productInfo.Title,
        hasFrontImage: !!this.productInfo.frontImage,
        hasFile: !!this.productInfo.file,
        frontImageFilename: this.productInfo.frontImage?.filename,
        fileFilename: this.productInfo.file?.filename
      });

      // Validar frontImage
      if (this.productInfo.frontImage?.base64) {
        console.log('✅ frontImage.base64 disponible:', this.productInfo.frontImage.filename);
      } else {
        console.warn('⚠️ No se encontró frontImage.base64');
      }

      // Validar file (PDF)
      if (this.productInfo.file?.base64) {
        console.log('✅ file.base64 (PDF) disponible:', this.productInfo.file.filename);
      } else {
        console.warn('⚠️ No se encontró file.base64 (PDF)');
      }

      // Validar que productInfo tenga los datos necesarios
      if (!this.productInfo.ProductInfo || !Array.isArray(this.productInfo.ProductInfo)) {
        console.warn('⚠️ ProductInfo no tiene la estructura esperada');
        this.productInfo.ProductInfo = [];
      }

      if (!this.productInfo.product || !Array.isArray(this.productInfo.product)) {
        console.warn('⚠️ product no tiene la estructura esperada');
        this.productInfo.product = [];
      }

      const resources = this.productInfo.ProductInfo.filter((pi: any) => pi.Property === 'Resource');
      
      this.productInfo.product.forEach(element => {
        if (element.Topics && Array.isArray(element.Topics)) {
          element.Topics = element.Topics.map((topic: any) => {
            return {
              ...topic,
              uploading: false,
              Resources: resources.filter((r: any) => r.Commentary === topic.id?.toString())
            }
          });
        } else {
          element.Topics = [];
        }
      });

      console.log('✅ Ebook cargado correctamente:', this.productInfo.Title);
    } catch (error: any) {
      console.error('❌ Error al cargar el ebook:', error);
      
      if (error.status === 404) {
        Notiflix.Notify.failure('Ebook no encontrado');
      } else if (error.status === 403) {
        Notiflix.Notify.failure('No tienes permiso para ver este ebook');
      } else if (error.status === 500) {
        Notiflix.Notify.failure('Error del servidor al cargar el ebook');
      } else {
        Notiflix.Notify.failure('Error al cargar el ebook');
      }
      
      this.route.navigateByUrl('/products/ebooks');
    }
  }

  // Método para abrir el PDF en una nueva ventana/pestaña
  openPdfModal() {
    const pdfUrl = this.getPdfUrl();
    
    if (!pdfUrl) {
      Notiflix.Notify.warning('No hay archivo PDF disponible para previsualizar');
      return;
    }
    
    console.log('📄 Abriendo PDF en nueva ventana');
    
    // Abrir en nueva ventana con dimensiones específicas
    const width = 1024;
    const height = 768;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;
    
    const newWindow = window.open(
      '', 
      'PDF Preview',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
    );
    
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Vista previa del PDF - ${this.productInfo?.Title || 'Ebook'}</title>
          <style>
            body { margin: 0; padding: 0; overflow: hidden; }
            iframe { border: none; width: 100%; height: 100vh; }
          </style>
        </head>
        <body>
          <iframe src="${pdfUrl}" type="application/pdf"></iframe>
        </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      Notiflix.Notify.failure('No se pudo abrir la ventana. Verifica que los pop-ups estén permitidos.');
    }
  }

  setActive() {
    if (!this.productInfo?.id) {
      console.error('❌ No se encontró el ID del ebook');
      Notiflix.Notify.failure('No se pudo publicar el ebook');
      return;
    }

    // Verificar que el ebook no esté ya publicado
    if (this.productInfo.Status?.toLowerCase() === 'publicado') {
      Notiflix.Notify.warning('El ebook ya está publicado');
      return;
    }

    Notiflix.Confirm.show(
      'Publicar ebook',
      '¿Estás seguro de que deseas publicar este ebook? Una vez publicado, estará visible para los usuarios.',
      'Sí, publicar',
      'Cancelar',
      () => {
        console.log('📤 Publicando ebook ID:', this.productInfo.id);
        
        this.productService.publishEbook(this.productInfo.id).subscribe({
          next: (response: any) => {
            console.log('✅ Ebook publicado exitosamente:', response);
            Notiflix.Notify.success('Ebook publicado correctamente');
            
            // Recargar la página o redirigir a la lista
            this.route.navigateByUrl('/products/ebooks');
          },
          error: (error: any) => {
            console.error('❌ Error al publicar el ebook:', error);
            
            if (error.status === 404) {
              Notiflix.Notify.failure('Ebook no encontrado');
            } else if (error.status === 403) {
              Notiflix.Notify.failure('No tienes permiso para publicar este ebook');
            } else if (error.status === 400) {
              Notiflix.Notify.failure(error.error?.message || 'El ebook no cumple con los requisitos para ser publicado');
            } else {
              Notiflix.Notify.failure('Error al publicar el ebook');
            }
          }
        });
      },
      () => {
        console.log('❌ Publicación cancelada por el usuario');
      }
    );
  }

  onSelectImages(event) {
    const file = event.target.files[0];

    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'images');
      this.productService.uploadFile2(formData)
        .subscribe(resolve => {
          if (resolve.FileUrl !== null) {
            const body = {
              id: this.productInfo.id,
              ImgFileUrl: resolve.ImageUrl
            };
            this.productService.updateCourse2(this.productInfo.id, body).subscribe((data: any) => {
              this.productInfo.ImgFileUrl = resolve.ImageUrl;
              Notiflix.Notify.success('Imagen cargada correctamente')
            });
          }
        });
    }
  }

  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  downloadEbook() {
    const ebook = this.productInfo.ProductInfo.find((pi: any) => pi.Value && pi.FileName);
  
    if (ebook) {
      const link = document.createElement('a');
      link.href = ebook.Value;
      link.download = ebook.FileName;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      Notiflix.Notify.failure('No se encontró el archivo del ebook.');
    }
  }  

  replaceEbook(event: any) {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'documents');
      this.productService.uploadFile2(formData)
        .subscribe(resolve => {
          if (resolve.ImageUrl !== null) {
            const body = {
              ProductId: this.productInfo.id,
              FileUrl: resolve.ImageUrl,
              FileName: file.name,
              FileSize: file.size,
              Type: file.type
            };
            this.productService.updateProductInfo(body).subscribe((data: any) => {
              this.productInfo.ProductInfo = this.productInfo.ProductInfo.map((pi: any) => {
                if (pi.Property === 'Resource') {
                  return {
                    ...pi,
                    Value: resolve.FileUrl,
                    FileName: file.name,
                    FileSize: file.size,
                    Type: file.type
                  };
                }
                return pi;
              });
              Notiflix.Notify.success('Archivo del ebook reemplazado correctamente');
            });
          }
        });
      }
  }
}
